/*
 * Phase 3: Gating for Patient Profile
 * modal in order to allow only users
 * with completed profile to access certain resources
 */
import React, { useState, useEffect } from "react";
import { User, Home, FileText, ChevronDown, ChevronUp } from "lucide-react";
import {
  createPatientAccountForCurrentUser,
  getPatientProfile,
} from "../APIs/patientService";
import { useCurrentUser } from "../APIs/UserContext";
import InsuranceDropdown from "../InsuranceDropdownComponent/InsuranceDropdown";
import AddressAutocomplete from "../Provider/AddressAutoComplete";

const CompletePatientProfileModal = ({
  isOpen,
  onClose,
  token,
  onCompleted,
  currentUser,
}) => {
  const { refreshUser } = useCurrentUser();
  const [sections, setSections] = useState({
    personal: true,
    address: true,
    document: false,
    insurance: false,
  });

  const [patientForm, setPatientForm] = useState({
    firstName: "",
    lastName: "",
    dateOfBirth: "",
    email: currentUser?.email || "",
    phoneNumber: "",
    addressLine: "",
    city: "",
    state: "",
    country: "",
    postalCode: "",
    documentType: "",
    documentCategory: "",
    documentFile: null,
    planId: "",
    memberId: "",
    groupId: "",
    coverageStart: "",
    coverageEnd: "",
    insuranceCard: null,
  });

  // ---- Phone formatting to match placeholder (###) ###-#### ----
  const formatPhone = (raw = "") => {
    const digits = raw.replace(/\D/g, "").slice(0, 10);
    const p1 = digits.slice(0, 3);
    const p2 = digits.slice(3, 6);
    const p3 = digits.slice(6, 10);
    if (digits.length <= 3) return p1;
    if (digits.length <= 6) return `(${p1}) ${p2}`;
    return `(${p1}) ${p2}-${p3}`;
  };

  useEffect(() => {
    if (currentUser?.email) {
      setPatientForm((prev) => ({ ...prev, email: currentUser.email }));
    }
  }, [currentUser]);

  useEffect(() => {
    if (!isOpen || !currentUser?.patientId) return;

    let ignore = false;

    const loadExistingProfile = async () => {
      try {
        const profile = await getPatientProfile(currentUser.patientId);
        if (ignore || !profile?.patient) return;

        const p = profile.patient;
        setPatientForm((prev) => ({
          ...prev,
          firstName: p.firstName || prev.firstName,
          lastName: p.lastName || prev.lastName,
          dateOfBirth: p.dateOfBirth || prev.dateOfBirth,
          email: p.email || currentUser.email || prev.email,
          phoneNumber: p.phoneNumber
            ? formatPhone(p.phoneNumber)
            : prev.phoneNumber,
          addressLine: p.addressLine || prev.addressLine,
          city: p.city || prev.city,
          state: p.state || prev.state,
          country: p.country || prev.country,
          postalCode: p.postalCode || prev.postalCode,
        }));
      } catch (error) {
        console.error("Failed to prefill patient profile:", error);
      }
    };

    loadExistingProfile();

    return () => {
      ignore = true;
    };
  }, [isOpen, currentUser]);

  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  if (!isOpen) return null;

  const toggleSection = (section) => {
    setSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const sectionStyle = (isOpen) =>
    `mb-6 border rounded-lg shadow-lg bg-white transition-all duration-300 ease-in-out ${
      isOpen ? "border-blue-500 bg-blue-50" : ""
    }`;

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "email") {
      // email is controlled by Keycloak; do not let user change it here
      return;
    }

    if (name === "phoneNumber") {
      setPatientForm((prev) => ({ ...prev, phoneNumber: formatPhone(value) }));
      return;
    }

    setPatientForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    setPatientForm((prev) => ({ ...prev, documentFile: e.target.files[0] }));
  };

  const handleInsuranceCardChange = (e) => {
    setPatientForm((prev) => ({ ...prev, insuranceCard: e.target.files[0] }));
  };

  const truncateStreet = (formatted) => {
    if (!formatted) return "";
    const i = formatted.indexOf(",");
    return (i === -1 ? formatted : formatted.slice(0, i)).trim();
  };

  const validate = () => {
    const newErrors = {};

    const requiredFields = [
      "firstName",
      "lastName",
      "dateOfBirth",
      "email",
      "phoneNumber",
      "addressLine",
    ];

    requiredFields.forEach((field) => {
      if (!patientForm[field]) {
        newErrors[field] = "This field is required";
      }
    });

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (patientForm.email && !emailRegex.test(patientForm.email)) {
      newErrors.email = "Enter a valid email";
    }

    const phonePretty = /^\(\d{3}\)\s\d{3}-\d{4}$/;
    if (patientForm.phoneNumber && !phonePretty.test(patientForm.phoneNumber)) {
      newErrors.phoneNumber = "Use format (555) 555-1234";
    }

    const insuranceTouched = !!(
      patientForm.planId ||
      patientForm.memberId ||
      patientForm.groupId ||
      patientForm.coverageStart ||
      patientForm.coverageEnd ||
      patientForm.insuranceCard
    );
    if (insuranceTouched) {
      if (!patientForm.planId) newErrors.planId = "Select an insurance plan";
      if (!patientForm.memberId) newErrors.memberId = "Member ID is required";
      if (!patientForm.coverageStart)
        newErrors.coverageStart = "Coverage start is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const buildInsuranceForm = () => {
    const { planId, memberId, groupId, coverageStart, coverageEnd } =
      patientForm;

    const insuranceTouched = !!(
      planId ||
      memberId ||
      groupId ||
      coverageStart ||
      coverageEnd ||
      patientForm.insuranceCard
    );

    if (!insuranceTouched) return null;

    return {
      planId,
      memberId,
      groupId,
      coverageStart,
      coverageEnd,
      // add any other fields your PatientInsuranceDTO expects
    };
  };

  /*const handleSubmit = async () => {
    if (!validate()) return;

    setSubmitting(true);
    try {
      const payload = {
        ...patientForm,
        phoneNumber: patientForm.phoneNumber.replace(/\D/g, ""), // digits only
      };

      const insuranceForm = buildInsuranceForm();

      const result = await createPatientAccountForCurrentUser(payload, {
        insuranceForm,
        documentFile: patientForm.documentFile,
        insuranceCardFile: patientForm.insuranceCard,
        token,
      });

      if (onCompleted) onCompleted(result);
      onClose();
    } catch (error) {
      console.error("Create account error:", error);
      const msg =
        error?.response?.data ||
        error?.message ||
        "There was an error creating your account.";
      alert(msg);
    } finally {
      setSubmitting(false);
    }
  };*/

  const handleSubmit = async () => {
    if (!validate()) return;

    setSubmitting(true);
    try {
      const payload = {
        ...patientForm,
        phoneNumber: patientForm.phoneNumber.replace(/\D/g, ""), // digits only
      };

      const insuranceForm = buildInsuranceForm();

      const result = await createPatientAccountForCurrentUser(payload, {
        insuranceForm,
        documentFile: patientForm.documentFile,
        insuranceCardFile: patientForm.insuranceCard,
        token,
      });

      await refreshUser?.();

      // ✅ Wait for onCompleted to finish before closing
      if (onCompleted) {
        await onCompleted(result);
      }

      onClose();
    } catch (error) {
      // 409 = profile already complete — treat as success and proceed
      if (error?.response?.status === 409) {
        await refreshUser?.();
        if (onCompleted) await onCompleted(null);
        onClose();
        return;
      }
      console.error("Create account error:", error);
      const msg =
        error?.response?.data ||
        error?.message ||
        "There was an error creating your account.";
      alert(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const renderError = (field) =>
    errors[field] && (
      <div className="text-red-600 text-sm">{errors[field]}</div>
    );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold">
              Complete Your Patient Profile
            </h1>
            <p className="text-sm text-gray-600 mt-1">
              To book appointments, save favorites, join waitlists, create
              alerts, and set preferences, we need a few more details.
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-800 px-2 text-xl"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {/* Personal Information */}
        <div className={sectionStyle(sections.personal)}>
          <button
            onClick={() => toggleSection("personal")}
            className="w-full flex items-center justify-between p-4 bg-gray-100 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-400 rounded-t-lg"
          >
            <div className="flex items-center space-x-2">
              <span className="bg-blue-600 text-white text-xs px-2 py-1 rounded-full">
                1
              </span>
              <User className="w-5 h-5" />
              <span className="font-medium">Personal Information</span>
            </div>
            {sections.personal ? <ChevronUp /> : <ChevronDown />}
          </button>
          <div
            className={`transition-all duration-300 ease-in-out overflow-hidden ${
              sections.personal
                ? "max-h-screen opacity-100"
                : "max-h-0 opacity-0"
            }`}
          >
            <div className="p-4 space-y-3">
              <div>
                <input
                  name="firstName"
                  value={patientForm.firstName}
                  onChange={handleChange}
                  className="w-full border p-2"
                  placeholder="First Name"
                  required
                  autoComplete="given-name"
                />
                {renderError("firstName")}
              </div>
              <div>
                <input
                  name="lastName"
                  value={patientForm.lastName}
                  onChange={handleChange}
                  className="w-full border p-2"
                  placeholder="Last Name"
                  required
                  autoComplete="family-name"
                />
                {renderError("lastName")}
              </div>
              <div>
                <input
                  name="dateOfBirth"
                  value={patientForm.dateOfBirth}
                  onChange={handleChange}
                  className="w-full border p-2"
                  placeholder="Date of Birth (YYYY-MM-DD)"
                  type="date"
                  required
                  autoComplete="bday"
                />
                {renderError("dateOfBirth")}
              </div>
              <div>
                <input
                  name="email"
                  value={patientForm.email}
                  className="w-full border p-2 bg-gray-100 text-gray-500 cursor-not-allowed"
                  placeholder="Email"
                  type="email"
                  required
                  autoComplete="email"
                  disabled
                />
                {renderError("email")}
              </div>
              <div>
                <input
                  name="phoneNumber"
                  value={patientForm.phoneNumber}
                  onChange={handleChange}
                  className="w-full border p-2"
                  placeholder="(555) 555-1234"
                  type="tel"
                  inputMode="tel"
                  maxLength={14}
                  required
                  autoComplete="tel"
                />
                {renderError("phoneNumber")}
              </div>
            </div>
          </div>
        </div>

        {/* Address */}
        <div className={sectionStyle(sections.address)}>
          <button
            onClick={() => toggleSection("address")}
            className="w-full flex items-center justify-between p-4 bg-gray-100 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-400 rounded-t-lg"
          >
            <div className="flex items-center space-x-2">
              <span className="bg-blue-600 text-white text-xs px-2 py-1 rounded-full">
                2
              </span>
              <Home className="w-5 h-5" />
              <span className="font-medium">Address</span>
            </div>
            {sections.address ? <ChevronUp /> : <ChevronDown />}
          </button>
          <div
            className={`transition-all duration-300 ease-in-out ${
              sections.address
                ? "max-h-screen opacity-100 overflow-visible"
                : "max-h-0 opacity-0 overflow-hidden"
            }`}
          >
            <div className="p-4 space-y-3 relative">
              <div>
                <label className="block text-sm text-gray-700 mb-1">
                  Address
                </label>
                <AddressAutocomplete
                  value={patientForm.addressLine}
                  placeholder="Start typing address…"
                  onSelect={(addr) => {
                    const line =
                      addr?.addressLine ||
                      addr?.line1 ||
                      addr?.streetAddress ||
                      "";
                    setPatientForm((prev) => ({
                      ...prev,
                      addressLine: truncateStreet(line),
                      city: addr?.city || prev.city,
                      state: addr?.state || prev.state,
                      country: addr?.country || prev.country,
                      postalCode:
                        addr?.postalCode || addr?.zipCode || prev.postalCode,
                    }));
                  }}
                />
              </div>

              <input
                name="addressLine"
                value={patientForm.addressLine}
                onChange={handleChange}
                className="hidden"
                placeholder="Address Line"
                required
                readOnly
                autoComplete="street-address"
              />
              {renderError("addressLine")}
            </div>
          </div>
        </div>

        {/* Insurance */}
        <div className={sectionStyle(sections.insurance)}>
          <button
            onClick={() => toggleSection("insurance")}
            className="w-full flex items-center justify-between p-4 bg-gray-100 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-400 rounded-t-lg"
          >
            <div className="flex items-center space-x-2">
              <span className="bg-blue-600 text-white text-xs px-2 py-1 rounded-full">
                4
              </span>
              <FileText className="w-5 h-5" />
              <span className="font-medium">Insurance (optional)</span>
            </div>
            {sections.insurance ? <ChevronUp /> : <ChevronDown />}
          </button>

          <div
            className={`transition-all duration-300 ease-in-out overflow-hidden ${
              sections.insurance
                ? "max-h-screen opacity-100"
                : "max-h-0 opacity-0"
            }`}
          >
            <div className="p-4 space-y-3">
              <div>
                <label className="block text-sm text-gray-700 mb-1">
                  Insurance Plan
                </label>
                <InsuranceDropdown
                  value={patientForm.planId}
                  onChange={(plan) =>
                    setPatientForm((prev) => ({
                      ...prev,
                      planId: plan?.planId || plan?.id || plan?.planName || "",
                    }))
                  }
                />
                {renderError("planId")}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <input
                    name="memberId"
                    value={patientForm.memberId}
                    onChange={handleChange}
                    className="w-full border p-2"
                    placeholder="Member ID"
                  />
                  {renderError("memberId")}
                </div>

                <div>
                  <input
                    name="groupId"
                    value={patientForm.groupId}
                    onChange={handleChange}
                    className="w-full border p-2"
                    placeholder="Group ID"
                  />
                </div>

                <div>
                  <label className="block text-sm text-gray-700 mb-1">
                    Coverage Start
                  </label>
                  <input
                    name="coverageStart"
                    type="date"
                    value={patientForm.coverageStart}
                    onChange={handleChange}
                    className="w-full border p-2"
                  />
                  {renderError("coverageStart")}
                </div>

                <div>
                  <label className="block text-sm text-gray-700 mb-1">
                    Coverage End
                  </label>
                  <input
                    name="coverageEnd"
                    type="date"
                    value={patientForm.coverageEnd}
                    onChange={handleChange}
                    className="w-full border p-2"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm text-gray-700 mb-1">
                  Insurance Card (optional)
                </label>
                <input
                  name="insuranceCard"
                  type="file"
                  onChange={handleInsuranceCardChange}
                  className="w-full"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Document */}
        <div className={sectionStyle(sections.document)}>
          <button
            onClick={() => toggleSection("document")}
            className="w-full flex items-center justify-between p-4 bg-gray-100 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-400 rounded-t-lg"
          >
            <div className="flex items-center space-x-2">
              <span className="bg-blue-600 text-white text-xs px-2 py-1 rounded-full">
                3
              </span>
              <FileText className="w-5 h-5" />
              <span className="font-medium">Document (optional)</span>
            </div>
            {sections.document ? <ChevronUp /> : <ChevronDown />}
          </button>
          <div
            className={`transition-all duration-300 ease-in-out overflow-hidden ${
              sections.document
                ? "max-h-screen opacity-100"
                : "max-h-0 opacity-0"
            }`}
          >
            <div className="p-4 space-y-3">
              <select
                name="documentType"
                value={patientForm.documentType}
                onChange={handleChange}
                className="w-full border p-2"
              >
                <option value="">Select Document Type</option>
                <option value="INSURANCE">Insurance</option>
                <option value="ID">ID Card</option>
                <option value="LAB_RESULT">Lab Result</option>
              </select>
              <select
                name="documentCategory"
                value={patientForm.documentCategory}
                onChange={handleChange}
                className="w-full border p-2"
              >
                <option value="">Select Document Category</option>
                <option value="PRIMARY">Primary</option>
                <option value="SECONDARY">Secondary</option>
              </select>
              <input
                name="documentFile"
                type="file"
                onChange={handleFileChange}
                className="w-full"
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 mt-4">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100"
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="px-6 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
          >
            {submitting ? "Saving..." : "Complete Profile"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CompletePatientProfileModal;
